import type {
  EventHandler,
  ResponseCreatedEvent,
  TextDeltaEvent,
  TextDoneEvent,
  ResponseCompletedEvent,
  ResponseFailedEvent,
  WebSearchCallInProgressEvent,
  WebSearchCallSearchingEvent,
  WebSearchCallCompletedEvent,
  ReasoningSummaryTextDeltaEvent,
  ReasoningSummaryTextDoneEvent,
} from './types';

// Handler: response.created
export const handleResponseCreated: EventHandler<ResponseCreatedEvent> = (
  state,
  event
) => ({
  ...state,
  status: 'streaming',
  responseId: event.response.id,
  model: event.response.model,
  conversationId: event.conversationId,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.in_progress
export const handleResponseInProgress: EventHandler = (state, event) => ({
  ...state,
  status: 'streaming',
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.output_text.delta
export const handleTextDelta: EventHandler<TextDeltaEvent> = (state, event) => ({
  ...state,
  textContent: state.textContent + event.delta,
  isThinking: false, // Model started responding, no longer thinking
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.output_text.done
export const handleTextDone: EventHandler<TextDoneEvent> = (state, event) => ({
  ...state,
  textContent: event.text, // Replace with final text
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.completed
export const handleResponseCompleted: EventHandler<ResponseCompletedEvent> = (
  state,
  event
) => ({
  ...state,
  status: 'completed',
  // Preserve accumulated text if outputText is empty (backend may not include it)
  textContent: event.response.outputText || state.textContent,
  usage: event.response.usage || null,
  reasoningSummary: event.response.reasoningSummary || state.reasoningSummary,
  annotations: event.response.annotations || [],
  isSearchingWeb: false,
  isThinking: false,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.failed
export const handleResponseFailed: EventHandler<ResponseFailedEvent> = (
  state,
  event
) => ({
  ...state,
  status: 'failed',
  error: event.error,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.web_search_call.in_progress
export const handleWebSearchInProgress: EventHandler<WebSearchCallInProgressEvent> = (
  state,
  event
) => ({
  ...state,
  isSearchingWeb: true,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.web_search_call.searching
export const handleWebSearchSearching: EventHandler<WebSearchCallSearchingEvent> = (
  state,
  event
) => ({
  ...state,
  isSearchingWeb: true,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.web_search_call.completed
export const handleWebSearchCompleted: EventHandler<WebSearchCallCompletedEvent> = (
  state,
  event
) => ({
  ...state,
  isSearchingWeb: false,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.reasoning_summary_text.delta
export const handleReasoningSummaryDelta: EventHandler<ReasoningSummaryTextDeltaEvent> = (
  state,
  event
) => ({
  ...state,
  isThinking: true,
  reasoningSummary: state.reasoningSummary + event.delta,
  lastSequenceNumber: event.sequenceNumber,
});

// Handler: response.reasoning_summary_text.done
export const handleReasoningSummaryDone: EventHandler<ReasoningSummaryTextDoneEvent> = (
  state,
  event
) => ({
  ...state,
  isThinking: false,
  reasoningSummary: event.text || state.reasoningSummary,
  lastSequenceNumber: event.sequenceNumber,
});

// Default handler for unknown events
export const handleUnknown: EventHandler = (state, event) => ({
  ...state,
  lastSequenceNumber: event.sequenceNumber,
});
